/**
 * FeeComparison — customer-facing "how our fees compare" card.
 *
 * Comparative advertising. Every figure + guardrail here is governed by the
 * harness golden contract `contracts/MARKETING_COMPLIANCE.md`:
 *   - platform fee on member dues ONLY (processing is separate + noted)
 *   - dated ("as of {UPDATED}") + sourced (see that contract's §4)
 *   - ranges where a competitor's rate varies by plan
 *   - "×more" multiples are exact ratios on the SAME metric (fee ÷ 0.4%)
 *   - PayPal classified honestly as a processor, not a membership platform
 *   - factual, non-disparaging
 * Before changing a number here, re-verify it and update MARKETING_COMPLIANCE.md.
 * Own rate (0.4% on member dues) is the golden contract in TIER_CAPABILITIES.md.
 */

const UPDATED = 'July 2026'

interface Row {
  provider: string
  /** Platform fee on recurring member dues. */
  fee: string
  /** How it compares to e404 — exact ratio on the same metric, or a note. */
  vs: string
  extra?: string
  highlight?: boolean
}

// Verified against MARKETING_COMPLIANCE.md §3 (sources §4). Multiples are exact:
// 10% ÷ 0.4% = 25×, 5% ÷ 0.4% = 12.5×, 2–3% ÷ 0.4% = 5–7.5×.
const ROWS: Row[] = [
  { provider: 'e404', fee: '0.4%', vs: 'Lowest platform fee', highlight: true },
  { provider: 'Join It', fee: '2–3%', vs: '5–7× more', extra: '+ $29–99/mo' },
  { provider: 'Ko-fi', fee: '5%', vs: '12× more' },
  { provider: 'Patreon', fee: '10%', vs: '25× more' },
  { provider: 'PayPal', fee: 'Processing only', vs: 'No squad tools' },
]

// What e404 bundles that a payment/membership tool doesn't — the "+ extras".
const EXTRAS = [
  'Roster & roles',
  'Events & sessions',
  'Tournaments + brackets',
  'Availability tracker',
  'Discord event sync',
  'Verified org status',
]

export function FeeComparison() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg/40">
      {/* Hero — how much cheaper, in one line */}
      <div className="border-b border-border/60 px-6 py-5">
        <p className="font-display text-[10px] font-extrabold tracking-[0.22em] text-primary uppercase">
          What platforms take from member dues
        </p>
        {/* "You keep 99.6%" is a BANNED claim — card processing and VAT are also
            deducted from the owner's payout. See contracts/MARKETING_COMPLIANCE.md §2.1b.
            The 0.4% platform-fee comparison below is unaffected and still true. */}
        <p className="font-display mt-3 text-2xl leading-tight font-bold text-fg md:text-3xl">
          A <span className="text-success">0.4%</span> platform fee.
        </p>
        <p className="mt-1.5 text-sm text-fg-muted">
          On £1,000/mo of dues, e404 takes <span className="font-semibold text-fg">£4</span>.
          Patreon takes <span className="font-semibold text-fg">£100</span> — 25× more.
        </p>
      </div>

      {/* Simple comparison — fee + how it stacks up vs e404 */}
      <table className="w-full text-left">
        <tbody>
          {ROWS.map((r) => (
            <tr key={r.provider} className={r.highlight ? 'bg-primary/[0.06]' : ''}>
              <td className="border-b border-border/40 px-6 py-3">
                <span
                  className={
                    r.highlight
                      ? 'font-display text-sm font-bold text-primary'
                      : 'text-sm text-fg'
                  }
                >
                  {r.provider}
                </span>
                {r.extra && (
                  <span className="ml-1.5 text-[11px] text-fg-subtle">{r.extra}</span>
                )}
              </td>
              <td className="border-b border-border/40 px-3 py-3 text-right">
                <span className="font-display text-sm font-bold tabular-nums text-fg">
                  {r.fee}
                </span>
              </td>
              <td className="border-b border-border/40 px-6 py-3 text-right">
                <span
                  className={
                    r.highlight
                      ? 'text-xs font-semibold text-success'
                      : 'text-xs text-fg-muted'
                  }
                >
                  {r.vs}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* + all the extra features */}
      <div className="bg-surface/30 px-6 py-5">
        <p className="text-sm font-semibold text-fg">
          <span className="text-success">+</span> And you get the whole squad platform —
          not just a payment cut.
        </p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {EXTRAS.map((e) => (
            <li
              key={e}
              className="rounded-md border border-border bg-bg/60 px-2.5 py-1 text-[11px] text-fg-muted"
            >
              {e}
            </li>
          ))}
        </ul>
      </div>

      {/* Compliance footnote (governed by MARKETING_COMPLIANCE.md) */}
      <p className="border-t border-border/40 px-6 py-3 text-[11px] leading-relaxed text-fg-subtle">
        Platform fees on member dues only. Payment processing (1.5–3.25% + 20p depending on
        the member&rsquo;s country) and VAT are separate and are deducted from the squadron
        owner&rsquo;s payout — as they are on every platform listed. PayPal is a payment
        processor, not a membership platform. Rates as of {UPDATED}; check each provider for
        current pricing.
      </p>
    </div>
  )
}
